/**
 * LanguageSuggestionBanner - Non-intrusive banner suggesting language switch
 * 
 * Shows on first visit if browser language differs from current UI language.
 * User must confirm to switch - no auto-switching.
 * 
 * FIXED: Now uses LocaleManager's STORAGE_KEY (keikka_locale)
 */
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/lib/context';
import { 
  LOCALE_CONFIG, 
  LOCALE_INFO, 
  normalizeLocale,
  isLocaleSupported,
} from '@/i18n';

// Use the same storage key as LocaleManager for consistency
const STORAGE_KEY = LOCALE_CONFIG.STORAGE_KEY; // 'keikka_locale'
const BANNER_DISMISSED_KEY = 'keikka_lang_banner_dismissed';

export const LanguageSuggestionBanner = () => {
  const { language, setLanguage, t } = useLanguage();
  const [suggestedLang, setSuggestedLang] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if user has already dismissed or made a choice
    const hasSeenBanner = localStorage.getItem(BANNER_DISMISSED_KEY);
    const hasChosenLanguage = localStorage.getItem(STORAGE_KEY);
    
    if (hasSeenBanner || hasChosenLanguage) {
      return;
    }

    // Detect browser language and normalize it
    const browserLang = navigator.language?.split('-')[0]?.toLowerCase();
    const normalizedLang = normalizeLocale(browserLang);
    
    if (!normalizedLang || normalizedLang === language) {
      return;
    }

    // Check if normalized browser language is supported
    if (isLocaleSupported(normalizedLang)) {
      setSuggestedLang(normalizedLang);
    }
  }, [language]);

  const handleSwitch = () => {
    setLanguage(suggestedLang);
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  const handleDismiss = () => {
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  if (!suggestedLang || dismissed) {
    return null;
  }

  const langInfo = LOCALE_INFO[suggestedLang] || {};
  const nativeName = langInfo.nativeName || suggestedLang;
  const flag = langInfo.flag || '🌐';

  // Get localized suggestion text
  const getSuggestionText = () => {
    switch (suggestedLang) {
      case 'et':
        return 'Kas soovid kasutada eesti keelt?';
      case 'fi':
        return 'Haluatko vaihtaa suomeksi?';
      case 'sv':
        return 'Vill du byta till svenska?';
      case 'nb':
        return 'Vil du bytte til norsk?';
      case 'ru':
        return 'Хотите переключиться на русский?';
      case 'de':
        return 'Möchten Sie zu Deutsch wechseln?';
      case 'fr':
        return 'Voulez-vous passer au français?';
      case 'es':
        return '¿Quieres cambiar al español?';
      default:
        return `Switch to ${nativeName}?`;
    }
  };

  const getButtonText = () => {
    switch (suggestedLang) {
      case 'et':
        return 'Jah, vaheta';
      case 'fi':
        return 'Kyllä, vaihda';
      case 'sv':
        return 'Ja, byt';
      case 'nb':
        return 'Ja, bytt';
      case 'ru':
        return 'Да, переключить';
      case 'de':
        return 'Ja, wechseln';
      case 'fr':
        return 'Oui, changer';
      case 'es':
        return 'Sí, cambiar';
      default:
        return 'Yes, switch';
    }
  };

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-auto max-w-md z-50 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="language-suggestion-banner"
    >
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 flex items-center gap-3">
        <div className="flex-shrink-0 text-2xl">{flag}</div>
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">
            {getSuggestionText()}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {t('language.detectedBrowser') || 'Detected from your browser'}
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button 
            size="sm" 
            onClick={handleSwitch}
            data-testid="language-switch-confirm"
          >
            {getButtonText()}
          </Button>
          <button
            onClick={handleDismiss}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
            aria-label={t('common.dismiss') || 'Dismiss'}
            data-testid="language-switch-dismiss"
          >
            <X className="h-4 w-4 text-gray-400" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LanguageSuggestionBanner;
